/**
 * Measurements a product carries (Petal_Some Ug., Events & Decor, 23 Sep
 * 2026): a tent 6 m by 12 m that seats 100 people, a chair with a 45 cm seat,
 * a rose bouquet whose Small has 12 stems. Rows of {size?, type, value, unit}:
 * a row with a size belongs to that size only (each florist types how many
 * stems HER Small has, and it's optional), a row without a size applies to
 * every size.
 *
 * The website shows sizes and measurements only when the shop wants it
 * (`showSizesOnWebsite`): an events business shows them for what it hires
 * out, not for its bouquets. Pure — the product form, the server and the
 * website share it.
 */

export type MeasurementType =
  | "length"
  | "width"
  | "height"
  | "depth"
  | "diameter"
  | "circumference"
  | "area"
  | "capacity"
  | "quantity";

const LENGTH_UNITS = ["mm", "cm", "m", "in", "ft"] as const;

/** Her list, type by type — the units each measurement can be given in. */
export const MEASUREMENT_TYPES: ReadonlyArray<{ value: MeasurementType; label: string; units: readonly string[] }> = [
  { value: "length", label: "Length", units: LENGTH_UNITS },
  { value: "width", label: "Width", units: LENGTH_UNITS },
  { value: "height", label: "Height", units: LENGTH_UNITS },
  { value: "depth", label: "Depth", units: LENGTH_UNITS },
  { value: "diameter", label: "Diameter", units: LENGTH_UNITS },
  { value: "circumference", label: "Circumference", units: LENGTH_UNITS },
  { value: "area", label: "Area", units: ["cm²", "m²", "ft²"] },
  { value: "capacity", label: "Capacity", units: ["person", "people", "seat", "litre", "kg"] },
  {
    value: "quantity",
    label: "Quantity",
    units: ["piece", "pieces", "set", "pair", "dozen", "bundle", "pack", "box", "roll", "bunch", "stem"],
  },
];

export interface MeasurementRow {
  /** The size this row belongs to ("Small"); absent = every size. */
  size?: string;
  type: MeasurementType;
  value: number;
  unit: string;
}

export interface ProductDimensions {
  rows: MeasurementRow[];
}

/** Most rows one product keeps. */
export const MAX_MEASUREMENT_ROWS = 40;

export function measurementTypeOf(type: unknown) {
  return MEASUREMENT_TYPES.find((t) => t.value === type);
}

/** The units a measurement type can be given in (none for an unknown type). */
export function unitsFor(type: unknown): readonly string[] {
  return measurementTypeOf(type)?.units ?? [];
}

/** "Height" */
export function measurementLabel(type: unknown): string {
  return measurementTypeOf(type)?.label ?? String(type ?? "");
}

/**
 * The rows worth keeping from a form or an API call: a known type, a unit that
 * type allows, a number above 0; blanks and junk dropped, sizes trimmed.
 * Undefined when nothing is left, so an emptied section clears.
 */
export function cleanDimensions(input: unknown): ProductDimensions | undefined {
  const raw = (input as { rows?: unknown } | null | undefined)?.rows;
  if (!Array.isArray(raw)) return undefined;
  const rows: MeasurementRow[] = [];
  for (const r of raw) {
    if (rows.length >= MAX_MEASUREMENT_ROWS) break;
    const type = (r as any)?.type;
    const unit = String((r as any)?.unit ?? "").trim();
    const value = Number((r as any)?.value);
    if (!measurementTypeOf(type) || !unitsFor(type).includes(unit)) continue;
    if (!Number.isFinite(value) || value <= 0) continue;
    const size = String((r as any)?.size ?? "").trim().slice(0, 40);
    rows.push({ ...(size ? { size } : {}), type, value: Math.round(value * 1000) / 1000, unit });
  }
  return rows.length ? { rows } : undefined;
}

// Word units read as words ("12 stems", "1 bunch", "100 people"); symbols
// stay as they are ("1.5 m", "72 m²").
const PLURAL: Record<string, string> = {
  piece: "pieces",
  pieces: "pieces",
  set: "sets",
  pair: "pairs",
  dozen: "dozen",
  bundle: "bundles",
  pack: "packs",
  box: "boxes",
  roll: "rolls",
  bunch: "bunches",
  stem: "stems",
  person: "people",
  people: "people",
  seat: "seats",
  litre: "litres",
};
const SINGULAR: Record<string, string> = { pieces: "piece", people: "person" };

/** "12 stems", "1 bunch", "1.5 m", "100 people", "1,000 seats" */
export function formatMeasurementValue(value: number, unit: string): string {
  const n = Number(value) || 0;
  const shown = n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (!(unit in PLURAL)) return `${shown} ${unit}`;
  return `${shown} ${n === 1 ? SINGULAR[unit] ?? unit : PLURAL[unit]}`;
}

/** The rows for a chosen size: that size's own rows, then the ones for every size. No size chosen = all rows. */
export function rowsForSize(rows: ReadonlyArray<MeasurementRow>, size?: string | null): MeasurementRow[] {
  if (!size) return [...rows];
  return [...rows.filter((r) => r.size === size), ...rows.filter((r) => !r.size)];
}

/**
 * Does the website show this product's sizes and measurements? The shop's own
 * choice when it made one. Otherwise an Events & Decor product shows them only
 * when it is for hire (a tent, chairs), and everything else shows them.
 */
export function showSizesOnWebsite(product: {
  showSizesOnWebsite?: boolean | null;
  industry?: string | null;
  hire?: { enabled?: boolean } | null;
}): boolean {
  if (typeof product.showSizesOnWebsite === "boolean") return product.showSizesOnWebsite;
  if (product.industry === "events") return product.hire?.enabled === true;
  return true;
}
